import React from 'react'
import { observer } from 'mobx-react-lite'
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom'
import clsx from 'clsx'

import { Header } from './Header'
import { Footer } from './Footer'
import { SideBarNavigation } from './SideBarNavigation'

import { AccountScreen } from './AccountScreen'
import { PrintRoute } from './Print/PrintRoute'
import { SyncLogsScreen } from './SyncLogsScreen'
import { ImportRoute } from './Import/ImportRoute'
import { OrdersRoute } from './Orders/OrdersRoute'
import { AdminUsersScreen } from './AdminUsersScreen'
import { AdminMsAppLifecycleScreen } from './AdminMsAppLifecycleScreen'
import { AdminRealizationReportsScreen } from './AdminRealizationReportsScreen'
import { SettingsRoute } from './Settings/SettingsRoute'
import { ProductsRoute } from './Products/ProductsRoute'
import { ProductDiffsScreen } from './ProductDiffsScreen'
import { MoySkladAccountRoute } from './Settings/MoySklad/MoySkladAccountRoute'

import { useStore } from '../hooks/storeHook'

import { appRoutes } from '../common/appRoutes'

import { isAppTypeMpSklad } from '../common/isAppTypeMpSklad'
import { mpCardRoutes } from '../common/mpCardRoutes'
import { NewAccountPage } from '../mpCard/pages/newAccountPage'
import { MpCardMainPage } from '../mpCard/pages/main'
import { MpCardRootRedirect } from '../mpCard/pages/main/mpCardRootRedirect'
import { MpCardFallbackRoute } from '../mpCard/pages/main/mpCardFallbackRoute'
import { CardPage } from '../mpCard/pages/cardPage'
import { CardsEditPage } from '../mpCard/pages/cardsEditPage'
import { CardsMovePage } from '../mpCard/pages/cardMovePage'
import { EditAccountPage } from '../mpCard/pages/editAccountPage'

// NOTE: Switch must not wrap routes in Fragment — RR v5 renders every matching route inside a Fragment.
const mpSkladRoutes = [
  <Route key="moySklad.account" path={appRoutes.MoySklad.account}>
    <MoySkladAccountRoute/>
  </Route>,

  <Route key="diffs" path={appRoutes.Diffs}>
    <ProductDiffsScreen/>
  </Route>,

  <Route key="account" path={appRoutes.Account}>
    <AccountScreen/>
  </Route>,

  <Route key="admin.users" path={appRoutes.AdminUsers}>
    <AdminUsersScreen/>
  </Route>,

  <Route key="admin.msAppLifecycle" path={appRoutes.AdminMsAppLifecycle}>
    <AdminMsAppLifecycleScreen/>
  </Route>,

  <Route key="admin.realizationReports" path={appRoutes.AdminRealizationReports}>
    <AdminRealizationReportsScreen/>
  </Route>,

  <Route key="moySkladApp" path={appRoutes.MoySkladApp.template}>
    <Redirect to={appRoutes.MoySklad.account}/>
  </Route>,

  <Route key="moySkladAppOld" path={appRoutes.MoySkladAppOld}>
    <Redirect to={appRoutes.MoySklad.account}/>
  </Route>,

  <Route key="store.print" path={appRoutes.Store.print.template}>
    <PrintRoute/>
  </Route>,

  <Route key="store.settings" path={appRoutes.Store.settings.template}>
    <SettingsRoute/>
  </Route>,

  <Route key="store.products" path={appRoutes.Store.products.template}>
    <ProductsRoute/>
  </Route>,

  <Route key="store.orders" path={appRoutes.Store.orders.template}>
    <OrdersRoute/>
  </Route>,

  <Route key="store.import" path={appRoutes.Store.import.template}>
    <ImportRoute/>
  </Route>,

  <Route key="store.syncLogs" path={appRoutes.Store.syncLogs.template}>
    <SyncLogsScreen/>
  </Route>,

  <Route key="fallback" path="*">
    <Redirect to={appRoutes.MoySklad.account}/>
  </Route>
]

const mpCardAppRoutes = [
  <Route key="settings.new" path={mpCardRoutes.settings.new.template} component={NewAccountPage}/>,

  <Route key="store.settings" path={appRoutes.Store.settings.template} component={EditAccountPage}/>,

  <Route key="newCard" path={mpCardRoutes.newCard.template} component={CardPage} exact/>,

  <Route key="editCards" path={mpCardRoutes.editCards.template} component={CardsEditPage} exact/>,

  <Route key="card" path={mpCardRoutes.card.template} component={CardPage} exact/>,

  <Route key="import" path={mpCardRoutes.import.template} component={CardPage} exact/>,

  <Route key="account" path={appRoutes.Account} component={AccountScreen}/>,

  <Route key="admin.users" path={appRoutes.AdminUsers} component={AdminUsersScreen}/>,

  <Route key="admin.msAppLifecycle" path={appRoutes.AdminMsAppLifecycle} component={AdminMsAppLifecycleScreen}/>,

  <Route key="moveCards" path={mpCardRoutes.moveCards.template} component={CardsMovePage} exact/>,

  <Route key="store" exact path={appRoutes.Store.template} component={MpCardMainPage}/>,

  <Route key="main" exact path={mpCardRoutes.main.template} component={MpCardRootRedirect}/>,

  <Route key="fallback" path="*">
    <MpCardFallbackRoute/>
  </Route>
]

export const AppRouter =
  observer(() => {
    const {hasMoySkladApp} = useStore()

    // NOTE: Can't use route hooks here
    // NOTE: react-router v6 is not worth upgrading to - lots of changes, not seeing benefits
    // NOTE: Tracking a route match here and storing in mobx doesn't work - it's still outdated

    return (
      <div className={clsx('App', {'ms-app': hasMoySkladApp})}>
        <BrowserRouter>
          <div className="app_container">
            <Header/>

            <div className="main_content">
              {isAppTypeMpSklad() && <SideBarNavigation/>}

              {/* NOTE: All routes must be inside a switch, because it renders exclusively - https://v5.reactrouter.com/web/api/Switch */}
              <Switch>
                {isAppTypeMpSklad() ? mpSkladRoutes : mpCardAppRoutes}
              </Switch>
            </div>

            <Footer/>
          </div>
        </BrowserRouter>
      </div>
    )
  })
